import {
  FC,
  Dispatch,
  SetStateAction,
  ReactElement,
  Children,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import {
  AdaptedEntity,
  Column,
  GenericEntityType,
  Pagination,
  TableCellProps,
  TemplateEntity,
  TextFieldValue,
  useLoaderNunjucks,
  WbResponsiveTableToolbar,
  wbSafeNunjucksApiRef,
  WbTable,
} from '@agilelab/plugin-wb-platform';
import {
  Box,
  createStyles,
  FormControl,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { cloneElement, isValidElement } from 'react';
import { isEqual } from 'lodash';
import { getLabelAndValue } from './utils';
import { AiToggle } from './MultiSelection/AiToggle';
import { useGeneralPurposePickerContext } from '../GeneralPurposePickerContext';
import { useApi } from '@backstage/core-plugin-api';

const renderRow = (
  parsedNunjuck: string,
  isAiBestMatchLabel: boolean,
  classes: ReturnType<typeof useStyles>,
) => {
  if (isAiBestMatchLabel) {
    return (
      <Typography>
        <span className={classes.boxBest}>{parsedNunjuck}</span>
      </Typography>
    );
  }
  return <Typography>{parsedNunjuck}</Typography>;
};

interface SelectableEntityTableProps {
  loading: boolean;
  data: GenericEntityType[];
  entities: TextFieldValue[];
  adapter: (entity: GenericEntityType) => AdaptedEntity;
  onChangeHandler: (entities: TextFieldValue[]) => void;
  filtersSection: () => JSX.Element;
  count: number;
  pagination: Pagination;
  columns: Column[];
  setPagination: Dispatch<SetStateAction<Pagination>>;
  multiple?: boolean;
  prevCursor?: string;
  nextCursor?: string;
  templateEntity: TemplateEntity;
  isAiMatchAvailable?: boolean;
  getAllFilteredEntities?: () => Promise<GenericEntityType[]>;
}

const useStyles = makeStyles(
  theme =>
    createStyles({
      formControl: {
        width: '100%',
        overflow: 'auto',
        height: '100%',
        justifyContent: 'space-between',
      },
      boxBest: {
        padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
        backgroundColor: theme.palette.white,
        border: 'solid 1px transparent',
        borderRadius: '4px',
        background:
          'linear-gradient( #ffffff, #ffffff) padding-box,linear-gradient(90deg, #EE72D3, #7C4DFF) border-box',
        whiteSpace: 'nowrap',
      },
      aiSuggestedRow: {
        backgroundColor: `${theme.palette.bkg.accent} !important`,
        '& td': {
          borderBottom: `1px solid ${theme.palette.accent.light}`,
        },
      },
      aiFilterButtonClass: {
        width: 'auto',
      },
    }),
  { name: 'selectionTable' },
);

export const SelectableEntityTable: FC<SelectableEntityTableProps> = ({
  loading,
  data,
  entities,
  adapter,
  onChangeHandler,
  filtersSection,
  multiple = false,
  pagination,
  setPagination,
  count,
  prevCursor,
  nextCursor,
  columns,
  templateEntity,
  isAiMatchAvailable = false,
  getAllFilteredEntities,
}) => {
  const classes = useStyles();
  const { isAiGenerated, fetchAiBestMatch, setAiState, setSuggestedItems } =
    useGeneralPurposePickerContext();
  const nunjucksApi = useApi(wbSafeNunjucksApiRef);

  // Preload nunjucks templates used in columns
  const nunjucksPaths = useMemo(() => columns.map(c => c.path), [columns]);
  const { isReady } = useLoaderNunjucks(nunjucksPaths);

  // Reference to keep track of AI added entities
  const aiAddedRef = useRef<TextFieldValue[]>([]);

  const getRowId = useCallback(
    (row: GenericEntityType): string => {
      const adaptedRow = getLabelAndValue(row, adapter(row), templateEntity);
      return JSON.stringify(adaptedRow.value);
    },
    [adapter, templateEntity],
  );

  const entityIdsSet = useMemo(() => {
    return new Set(entities.map(entity => JSON.stringify(entity.value)));
  }, [entities]);

  const selectedItems = useMemo(() => {
    return data.filter(row => {
      const rowId = getRowId(row);
      return entityIdsSet.has(rowId);
    });
  }, [data, entityIdsSet, getRowId]);

  const setSelectedItems = useCallback(
    (newSelectedItems: GenericEntityType[]) => {
      // convert new selected items to TextFieldValue[]
      const newEntitiesFromCurrentPage = newSelectedItems.map(row => {
        const adaptedRow = getLabelAndValue(row, adapter(row), templateEntity);
        if (templateEntity.displayName) {
          adaptedRow.kindDisplayName = templateEntity.displayName;
        }
        return adaptedRow;
      });

      // In single mode the selection is always replaced by the new item.
      // In multiple mode, selections from other pages are preserved.
      const currentPageIds = data.map(row => getRowId(row));
      const entitiesFromOtherPages = multiple
        ? entities.filter(entity => {
            const entityId = JSON.stringify(entity.value);
            return !currentPageIds.includes(entityId);
          })
        : [];

      const allEntities = [
        ...entitiesFromOtherPages,
        ...newEntitiesFromCurrentPage,
      ];

      onChangeHandler(allEntities);
    },
    [
      entities,
      data,
      getRowId,
      adapter,
      templateEntity,
      onChangeHandler,
      multiple,
    ],
  );

  const addAiMatches = useCallback(
    async (options: GenericEntityType[]) => {
      const result = await fetchAiBestMatch(options);
      const aiBestMatch =
        result?.map(row =>
          getLabelAndValue(row, adapter(row), templateEntity, true),
        ) ?? [];

      // Add AI best matches that are not already in the selected entities
      const toAdd = aiBestMatch.filter(
        ai => !entities.some(e => isEqual(e.value, ai.value)),
      );
      aiAddedRef.current = toAdd;
      return [...entities, ...toAdd];
    },
    [fetchAiBestMatch, adapter, templateEntity, entities],
  );

  const removeAiMatches = useCallback(() => {
    const newSelection = entities.filter(
      e => !aiAddedRef.current.some(ai => isEqual(e, ai)),
    );
    aiAddedRef.current = [];
    return newSelection;
  }, [entities]);

  const handleChange = useCallback(
    async (checked?: boolean) => {
      setAiState(checked ?? false);
      setSuggestedItems([]);

      if (checked) {
        // fetch all available entities not paginated
        const options = (await getAllFilteredEntities?.()) ?? [];
        const newEntities = await addAiMatches(options);
        onChangeHandler(newEntities);
      } else {
        const newEntities = removeAiMatches();
        onChangeHandler(newEntities);
      }
    },
    [
      setAiState,
      setSuggestedItems,
      getAllFilteredEntities,
      addAiMatches,
      removeAiMatches,
      onChangeHandler,
    ],
  );

  const handleChangePage = useCallback(
    (newPage: number) => {
      setPagination(v => ({
        limit: v.limit ?? 0,
        currentPage: newPage,
        cursor: pagination.currentPage > newPage ? prevCursor : nextCursor,
      }));
    },
    [setPagination, pagination.currentPage, prevCursor, nextCursor],
  );

  const handleChangeRowsPerPage = useCallback(
    (limit: number) => {
      setPagination({
        limit: limit,
        currentPage: 0,
        cursor: undefined,
      });
    },
    [setPagination],
  );

  const tableColumns = useMemo((): TableCellProps<GenericEntityType>[] => {
    return columns.map((column, idx) => ({
      field: column.name,
      fieldRender: (row: GenericEntityType) => {
        // Render nunjucks template with row data, before that we load the template in the useLoaderNunjucks hook
        const parsedNunjucks = isReady
          ? nunjucksApi.render(column.path, row)
          : '-';

        const isAiBestMatch = isAiGenerated(row);
        // for the first column, if the row is AI generated, we add the "Best match" badge
        if (idx === 0 && isAiBestMatch) {
          return (
            <Box
              key={column.name}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography>{parsedNunjucks || '-'}</Typography>
              <Typography className={classes.boxBest} variant="caption">
                Best match
              </Typography>
            </Box>
          );
        }
        return renderRow(
          parsedNunjucks ?? '-',
          column.path === 'aiBestMatchLabel',
          classes,
        );
      },
      headerName: column.name,
    }));
  }, [classes, columns, isAiGenerated, nunjucksApi, isReady]);

  const sortedData = useMemo(() => {
    if (!isAiMatchAvailable) return data;

    const aiRows: GenericEntityType[] = [];
    const normalRows: GenericEntityType[] = [];

    data.forEach(row => {
      if (isAiGenerated(row)) {
        aiRows.push(row);
      } else {
        normalRows.push(row);
      }
    });

    return [...aiRows, ...normalRows];
  }, [data, isAiMatchAvailable, isAiGenerated]);

  const aiToggleComponent = useMemo(
    () => (
      <AiToggle
        handleChange={handleChange}
        aiFilterButtonClass={classes.aiFilterButtonClass}
      />
    ),
    [handleChange, classes.aiFilterButtonClass],
  );

  const section = filtersSection();

  const filtersSectionWithAI = useMemo(() => {
    if (!isAiMatchAvailable) {
      return section;
    }

    const aiToggleWrapper = (
      <WbResponsiveTableToolbar.Actions key="ai-toggle">
        {aiToggleComponent}
      </WbResponsiveTableToolbar.Actions>
    );

    if (isValidElement(section) && !!(section as ReactElement).props.children) {
      return cloneElement(section, {}, [
        ...Children.toArray((section as ReactElement).props.children),
        aiToggleWrapper,
      ]);
    }

    return cloneElement(section, {}, [aiToggleWrapper]);
  }, [isAiMatchAvailable, section, aiToggleComponent]);

  const getRowClassName = useCallback(
    (row: GenericEntityType) => {
      return isAiGenerated(row) ? classes.aiSuggestedRow : undefined;
    },
    [isAiGenerated, classes.aiSuggestedRow],
  );

  return (
    <>
      {filtersSectionWithAI}
      <FormControl className={classes.formControl}>
        <WbTable<GenericEntityType>
          styles={{
            container: {
              flex: 1,
            },
          }}
          selection={multiple ? 'multiple' : 'single'}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          components={{
            tableLoader: { loading },
            tableContent: {
              columns: tableColumns,
              rows: sortedData,
              getRowId,
              rowDynamicClassName: getRowClassName,
            },
          }}
          pagination={{
            countlessOptions: pagination.countlessOptions,
            rowsPerPageOptions: [5, 10, 25],
            count: count,
            limit: pagination.limit ?? 0,
            currentPage: pagination.currentPage,
            onPageChange: handleChangePage,
            onRowsPerPageChange: handleChangeRowsPerPage,
          }}
        />
      </FormControl>
    </>
  );
};
